/**
 * BUSINESS RELATION CARD — card personalizzata per RelationPanel quando
 * il targetType e' Business. Contenuto: nome, stellina dell'azienda
 * principale del contatto (al piu' una per Source/Business, vedi TS-15; da
 * non confondere con il contatto preferito dell'azienda, FILO-25), dominio
 * cliccabile, telefono primario.
 *
 * Fa un getById su businessApi al mount per leggere domain + phones: la
 * label risolta dal registry contiene solo il name.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ensureHttpsPrefix } from '@crm/shared';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { businessApi } from '../entities/business/businessApi';
import type { Business } from '../entities/business/businessTypes';
import type { RelationItemContext } from './RelationPanel';

interface BusinessRelationCardProps {
  ctx: RelationItemContext;
  /** businessId = ctx.relationship.classPK2 (lato opposto alla source). */
  businessId: number;
}

export function BusinessRelationCard({ ctx, businessId }: BusinessRelationCardProps) {
  const { t } = useTranslation();
  const [business, setBusiness] = useState<Business | null>(null);

  useEffect(() => {
    let cancelled = false;
    businessApi
      .getById(businessId)
      .then((b) => { if (!cancelled) setBusiness(b); })
      .catch(() => { if (!cancelled) setBusiness(null); });
    return () => { cancelled = true; };
  }, [businessId]);

  const primaryPhone = business?.phones?.find((p) => p.primary)?.number
    ?? business?.phones?.[0]?.number
    ?? null;
  // Normalizza il dominio in URL HTTP: il campo in DB e' "example.com",
  // il browser ha bisogno dello schema per aprirlo.
  const domainHref = business?.domain ? ensureHttpsPrefix(business.domain) : null;

  const name = business?.name ?? ctx.label;
  const emblem = name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '—';

  return (
    <div className="relation-card">
      {/* Emblema iniziali + colonna nome/dominio/telefono + azioni: tutto su
          una riga (stile mockup .rel-item), stella e rimuovi a destra. */}
      <span className="rel-emblem">{emblem}</span>
      <div className="relation-card__main">
        <Link to={ctx.path} className="relation-link relation-card__title">{name}</Link>
        {domainHref && (
          <a href={domainHref} target="_blank" rel="noopener noreferrer" className="relation-card__domain">
            {business?.domain}
          </a>
        )}
        {primaryPhone && <div className="relation-card__phone">{primaryPhone}</div>}
        {(ctx.relationship.reverseDescription || ctx.relationship.description) && (
          <div className="relation-card__desc">
            {ctx.relationship.reverseDescription || ctx.relationship.description}
          </div>
        )}
      </div>
      {ctx.requestSetPrimary && (
        <IconButton
          className={`rel-star${ctx.isPrimary ? ' rel-star--on' : ''}`}
          size="small"
          onClick={() => ctx.requestSetPrimary!(!ctx.isPrimary)}
          aria-label={ctx.isPrimary ? t('relationship.unsetPrimaryBusiness') : t('relationship.setPrimaryBusiness')}
          title={ctx.isPrimary ? t('relationship.unsetPrimaryBusiness') : t('relationship.setPrimaryBusiness')}
        >
          {ctx.isPrimary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </IconButton>
      )}
      {ctx.requestEdit && (
        <IconButton
          size="small"
          onClick={ctx.requestEdit}
          aria-label={t('relationship.editDescriptions')}
          title={t('relationship.editDescriptions')}
          className="relation-card__edit"
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton
        size="small"
        onClick={ctx.requestDelete}
        aria-label={t('relationship.removeLabel')}
        className="relation-card__remove"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
}
